import { SKILLS, SkillId } from '../config';

export interface SkillState {
  owned: boolean;
  level: number;
  cooldownRemaining: number; // ms remaining
}

export class SkillManager {
  skills: Record<SkillId, SkillState>;

  constructor() {
    this.skills = {} as Record<SkillId, SkillState>;
    for (const id of Object.keys(SKILLS) as SkillId[]) {
      this.skills[id] = {
        owned: false,
        level: 0,
        cooldownRemaining: 0,
      };
    }
  }

  buySkill(id: SkillId): boolean {
    if (this.skills[id].owned) return false;
    this.skills[id].owned = true;
    this.skills[id].level = 1;
    return true;
  }

  upgradeSkill(id: SkillId): boolean {
    if (!this.skills[id].owned) return false;
    this.skills[id].level++;
    return true;
  }

  canActivate(id: SkillId): boolean {
    const skill = this.skills[id];
    return skill.owned && skill.cooldownRemaining <= 0;
  }

  activateSkill(id: SkillId): boolean {
    if (!this.canActivate(id)) return false;
    const config = SKILLS[id];
    const level = this.skills[id].level;
    const cooldown = config.baseCooldown - (level - 1) * config.cooldownReduction;
    this.skills[id].cooldownRemaining = Math.max(cooldown, 7000); // Min 7s cooldown
    return true;
  }

  getCooldownPercent(id: SkillId): number {
    const skill = this.skills[id];
    if (!skill.owned || skill.cooldownRemaining <= 0) return 0;
    const config = SKILLS[id];
    const level = skill.level;
    const maxCooldown = Math.max(config.baseCooldown - (level - 1) * config.cooldownReduction, 7000);
    return skill.cooldownRemaining / maxCooldown;
  }

  update(delta: number): void {
    for (const id of Object.keys(this.skills) as SkillId[]) {
      if (this.skills[id].cooldownRemaining > 0) {
        this.skills[id].cooldownRemaining -= delta;
        if (this.skills[id].cooldownRemaining < 0) {
          this.skills[id].cooldownRemaining = 0;
        }
      }
    }
  }

  isOwned(id: SkillId): boolean {
    return this.skills[id].owned;
  }

  getLevel(id: SkillId): number {
    return this.skills[id].level;
  }

  // Get computed values for a skill at its current level
  getSkillValue(id: SkillId, stat: string): number {
    const config = SKILLS[id] as any;
    const level = this.skills[id].level;

    switch (id) {
      case 'poison_arrows':
        if (stat === 'damage') return Math.round(config.baseDamage * Math.pow(config.damageGrowth, level - 1));
        if (stat === 'duration') return config.baseDuration + (level - 1) * config.durationPerLevel;
        break;
      case 'rock_rain':
        if (stat === 'damage') return Math.round(config.baseDamage * Math.pow(config.damageGrowth, level - 1));
        if (stat === 'radius') return config.radius;
        break;
      case 'gold_rush':
        if (stat === 'multiplier') return config.baseMultiplier + (level - 1) * config.multiplierPerLevel;
        if (stat === 'duration') return config.baseDuration + (level - 1) * config.durationPerLevel;
        break;
      case 'lightning_strike':
        if (stat === 'damage') return Math.round(config.baseDamage * Math.pow(config.damageGrowth, level - 1));
        if (stat === 'chains') return config.baseChainCount + (level - 1) * config.chainsPerLevel;
        break;
    }
    return 0;
  }

  reset(): void {
    for (const id of Object.keys(this.skills) as SkillId[]) {
      this.skills[id] = { owned: false, level: 0, cooldownRemaining: 0 };
    }
  }

  // ---- Serialization ----

  getState(): Record<string, { owned: boolean; level: number }> {
    const state: Record<string, { owned: boolean; level: number }> = {};
    for (const [id, skill] of Object.entries(this.skills)) {
      state[id] = { owned: skill.owned, level: skill.level };
    }
    return state;
  }

  loadState(state: Record<string, { owned: boolean; level: number }>): void {
    for (const [id, data] of Object.entries(state)) {
      if (this.skills[id as SkillId]) {
        this.skills[id as SkillId].owned = data.owned;
        this.skills[id as SkillId].level = data.level;
        this.skills[id as SkillId].cooldownRemaining = 0;
      }
    }
  }
}
